import type { MutableRefObject } from 'react';
import { createContext, useContext } from 'react';
import type { LiveAvatarSession, SessionState } from '@heygen/liveavatar-web-sdk';
import type { TTSConfig } from '@trinity/chat/src/types/tts-config';

/**
 * Shape of the context exposed to descendants.
 *
 * - `sessionRef` — mutable ref to the live SDK session instance. Components that
 *   need to drive the avatar (e.g. HeyGenResponseBridge calling repeatAudio) read
 *   this ref rather than re-rendering on every session event.
 * - `sessionState` — reactive SessionState enum value for UI (loading, connected, etc.)
 * - `isStreamReady` — becomes true once SESSION_STREAM_READY fires; the <LiveAvatar>
 *   video element listens for this to call session.attach().
 * - `isAvatarTalking` — true while the avatar is actively speaking; useful for UI.
 * - `isMuted` — whether the avatar video element is muted (audio output silenced).
 * - `toggleMute` — toggles the muted state on the avatar video element.
 */
export type LiveAvatarContextValue = {
  sessionRef: MutableRefObject<LiveAvatarSession | null>;
  sessionState: SessionState;
  isStreamReady: boolean;
  isAvatarTalking: boolean;
  ttsConfig: TTSConfig | null;
  isMuted: boolean;
  toggleMute: () => void;
  /**
   * Shared ref holding all setTimeout IDs scheduled by paceRepeatAudio in
   * HeyGenResponseBridge. To cancel all queued audio segments (e.g. when the
   * user clicks Interrupt), forEach clearTimeout on it and reset to [].
   */
  pendingAudioTimeoutsRef: MutableRefObject<ReturnType<typeof setTimeout>[]>;
};

export const LiveAvatarContext = createContext<LiveAvatarContextValue | null>(null);

/**
 * Hook to consume the LiveAvatarContext. Throws if called outside a
 * LiveAvatarContextProvider — this is intentional so that missing provider
 * wiring fails loudly in development.
 */
export function useLiveAvatarContext(): LiveAvatarContextValue {
  const ctx = useContext(LiveAvatarContext);
  if (ctx === null) {
    throw new Error(
      'useLiveAvatarContext must be used within a LiveAvatarContextProvider',
    );
  }
  return ctx;
}

/**
 * Safe version of useLiveAvatarContext — returns null when the context is not
 * available (i.e. outside a LiveAvatarContextProvider). Used by LiveAvatar and
 * HeyGenResponseBridge so they silently no-op while the session token is still
 * loading or when no avatar config has been provided.
 */
export function useLiveAvatarContextOptional(): LiveAvatarContextValue | null {
  return useContext(LiveAvatarContext);
}
